export const M8_UNIT_2 = {
  id: "m8-u2-adventures-achievements",
  title: "Unit 2: Adventures & Achievements",
  grammarText: "Present Perfect with modifying adverbs (just/already/yet/still) + Present Perfect Continuous (I've been painting for hours) + PPC Questions (How long have you been...?).",
  grammarImage: "/grammar/m8-u2.jpg",
  sets: [

    // SET 1: Adventure Achievements (just/already)
    {
      id: "m8-u2-s1",
      title: "Set 1: Adventure Achievements",
      context: `*EducaT Adventure Club Reunion*\n\nEveryone sharing recent adventures. "I've just returned from skydiving!" "Already climbed three mountains this year!"\n\n*Club meeting room*\n\nExcited voices overlapping. Photos everywhere.`,
      questions: [
        {type: "match", q: "Match adverbs to meaning:", pairs: [{left: "just", right: "short time ago"}, {left: "already", right: "earlier than expected"}, {left: "yet", right: "expected to happen"}, {left: "still", right: "ongoing situation"}], a: "completed", successTip: "ADVERB TIMING! 'Just' = moments ago. UK/US same placement."},
        {type: "order", q: "Traduce: 'Acabo de regresar de buceo'", words: ["I've", "just", "returned", "from", "scuba", "diving."], a: "I've just returned from scuba diving.", extra: ["already", "I return"], tip: "Just = between have + past participle."},
        {type: "mc", q: "I've ___ my bags. Ready to go!", options: ["already packed", "yet packed"], a: "already packed"},
        {type: "mc", q: "Position: I've ___ three countries this year.", options: ["already visited", "visited already"], a: "already visited", tip: "Already before main verb."},
        {type: "input", q: "I've ___ climbed Harbour Bridge!", a: "just"},
        {type: "match", q: "Adventure sports:", pairs: [{left: "scuba diving", right: "underwater"}, {left: "hang gliding", right: "air + harness"}, {left: "skydiving", right: "parachute"}, {left: "safari", right: "big cats"}], a: "completed"},
        {type: "order", q: "Traduce: 'Ya he probado windsurfing'", words: ["I've", "already", "tried", "windsurfing."], a: "I've already tried windsurfing.", extra: ["I try", "done"], tip: "Already = ya (completed earlier)."},
        {type: "mc", q: "She's ___ gone snorkeling.", options: ["just", "yet"], a: "just"},
        {type: "input", q: "Have you ___ been on safari?", a: "already"},
        {type: "order", q: "Club intro: I've just returned from Hawaii and already ___ skydiving!", words: ["tried"], a: "I've just returned from Hawaii and already tried skydiving!", successTip: "ADVENTURE BOSS! 'Just/already' show sequence. 'I've just landed!' Native energy! 🪂"}
      ]
    },

    // SET 2: Yet and Still (negative/questions)
    {
      id: "m8-u2-s2",
      title: "Set 2: Unfinished Adventures",
      context: `*Planning next trips*\n\n"I haven't been to Australia yet." "Still haven't seen dolphins!" Discussing pending bucket list items.\n\n*Dream board session*\n\nPins on world map. Wishlist growing.`,
      questions: [
        {type: "match", q: "Negative adverbs:", pairs: [{left: "yet", right: "end of sentence"}, {left: "still", right: "after subject"}, {left: "haven't...yet", right: "negative only"}, {left: "still haven't", right: "emphasize ongoing"}], a: "completed"},
        {type: "order", q: "Traduce: 'Todavía no he visto delfines'", words: ["I", "haven't", "seen", "dolphins", "yet."], a: "I haven't seen dolphins yet.", extra: ["I don't see", "still"], tip: "Yet = end of negative sentence."},
        {type: "mc", q: "I ___ tried hang gliding.", options: ["still haven't", "haven't yet"], a: "still haven't", tip: "Still after subject, before haven't."},
        {type: "mc", q: "Question: Have you been to Kenya ___?", options: ["yet", "still"], a: "yet"},
        {type: "input", q: "She ___ hasn't learned to surf.", a: "still"},
        {type: "match", q: "Positions:", pairs: [{left: "just/already", right: "before main verb"}, {left: "yet", right: "sentence end"}, {left: "still", right: "after subject"}, {left: "never", right: "before main verb"}], a: "completed", successTip: "POSITION MASTER! 'Still waiting / Not yet arrived / Just finished.' Natural flow!"},
        {type: "order", q: "Traduce: 'Aún sigo esperando mi viaje'", words: ["I'm", "still", "waiting", "for", "my", "trip."], a: "I'm still waiting for my trip.", extra: ["yet waiting"], tip: "Still = todavía (ongoing)."},
        {type: "mc", q: "Negative: I ___ gone scuba diving.", options: ["haven't yet", "still haven't"], a: "haven't yet"},
        {type: "input", q: "Have you booked your flight ___?", a: "yet"},
        {type: "order", q: "Wishlist: I still haven't been on safari but ___ booked my trip!", words: ["I've", "already"], a: "I still haven't been on safari but I've already booked my trip!", successTip: "CONTRAST PRO! 'Still waiting BUT already planned!' Mix adverbs like natives. 🌍"}
      ]
    },

    // SET 3: Adventure Timeline
    {
      id: "m8-u2-s3",
      title: "Set 3: Adventure Timeline",
      context: `*Sharing photo album*\n\n"I've already visited Red Sea, just returned from Interlaken, still haven't tried windsurfing yet."\n\n*Photo slideshow*\n\nChronological order. Gaps visible.`,
      questions: [
        {type: "match", q: "Timeline order:", pairs: [{left: "already", right: "completed before now"}, {left: "just", right: "moments ago"}, {left: "yet", right: "expected future"}, {left: "still", right: "continues now"}], a: "completed"},
        {type: "order", q: "Traduce: 'Ya escalé el puente'", words: ["I've", "already", "climbed", "the", "bridge."], a: "I've already climbed the bridge.", extra: ["I climb", "just"], tip: "Already = past completion."},
        {type: "mc", q: "She's ___ from the Blue Mountains.", options: ["just returned", "returned just"], a: "just returned"},
        {type: "mc", q: "Complete: I've learned to surf but ___ tried skydiving.", options: ["haven't yet", "just haven't"], a: "haven't yet"},
        {type: "input", q: "I'm ___ planning my safari trip.", a: "still"},
        {type: "match", q: "Adventure vocab:", pairs: [{left: "snorkeling", right: "surface water"}, {left: "scuba diving", right: "deep underwater"}, {left: "hang gliding", right: "sky gliding"}, {left: "windsurfing", right: "board + sail"}], a: "completed"},
        {type: "order", q: "Traduce: 'No he visto ballenas aún'", words: ["I", "haven't", "seen", "whales", "yet."], a: "I haven't seen whales yet.", extra: ["still", "I don't see"], tip: "Yet = aún (negative end)."},
        {type: "mc", q: "I've ___ booked my next adventure!", options: ["already", "yet"], a: "already"},
        {type: "input", q: "Have you ___ finished packing?", a: "already"},
        {type: "order", q: "Full story: I've just booked safari, already packed, but still ___ my visa!", words: ["haven't", "got"], a: "I've just booked safari, already packed, but still haven't got my visa!", successTip: "TIMELINE FLUENT! Mix all 4 adverbs naturally. 'Just/already/yet/still' = native precision! ⏰"}
      ]
    },

    // SET 4: PPC Intro (ongoing activities)
    {
      id: "m8-u2-s4",
      title: "Set 4: Home Projects",
      context: `*Neighbor chat over fence*\n\n"What have you been doing? Covered in paint!" "I've been tiling the bathroom all morning!"\n\n*DIY weekend*\n\nHammers, brushes, exhausted faces.`,
      questions: [
        {type: "match", q: "PPC structure:", pairs: [{left: "have/has been", right: "+ing"}, {left: "I've been", right: "painting"}, {left: "She's been", right: "cooking"}, {left: "They've been", right: "working"}], a: "completed", successTip: "PPC FORMED! Have/has + been + ing. Shows ongoing recent activity."},
        {type: "order", q: "Traduce: 'He estado pintando la casa'", words: ["I've", "been", "painting", "the", "house."], a: "I've been painting the house.", extra: ["I paint", "I painted"], tip: "Been + ing = ongoing recent."},
        {type: "mc", q: "Evidence: You're wet! ___ in the rain?", options: ["Have you been walking", "Did you walk"], a: "Have you been walking"},
        {type: "mc", q: "She ___ the kitchen all day.", options: ["has been tiling", "has tiling"], a: "has been tiling"},
        {type: "input", q: "I've ___ fixing the bathtub.", a: "been"},
        {type: "match", q: "Home improvements:", pairs: [{left: "tiling", right: "floor/walls"}, {left: "painting", right: "walls/ceilings"}, {left: "fixing bathtub", right: "plumbing"}, {left: "putting up shelves", right: "storage"}], a: "completed"},
        {type: "order", q: "Traduce: 'Ha estado cocinando'", words: ["She's", "been", "cooking."], a: "She's been cooking.", extra: ["She cooks"], tip: "PPC enfatiza duración."},
        {type: "mc", q: "We ___ all morning.", options: ["have been cleaning", "are cleaning"], a: "have been cleaning"},
        {type: "input", q: "They've been ___ the carpet.", a: "fitting"},
        {type: "order", q: "Neighbor evidence: You're exhausted! Been ___ the lawn?", words: ["mowing"], a: "You're exhausted! Been mowing the lawn?", successTip: "EVIDENCE TALK! See result → ask PPC. 'Covered in paint? Been decorating?' B2 natural! 🎨"}
      ]
    },

    // SET 5: For and Since (duration)
    {
      id: "m8-u2-s5",
      title: "Set 5: Time Markers",
      context: `*Exhausted after long project*\n\n"I've been working for 5 hours! Since 8am!" Explaining why tired.\n\n*Coffee break*\n\nClock shows 1pm. Started early.`,
      questions: [
        {type: "match", q: "For vs Since:", pairs: [{left: "for", right: "duration"}, {left: "since", right: "start point"}, {left: "for 3 hours", right: "quantity"}, {left: "since Monday", right: "time/date"}], a: "completed", successTip: "FOR/SINCE! For = cuánto tiempo. Since = desde cuándo. Never mix!"},
        {type: "order", q: "Traduce: 'Por tres horas'", words: ["for", "three", "hours"], a: "for three hours", extra: ["since", "during"], tip: "For + duration (hours/days/years)."},
        {type: "mc", q: "I've been painting ___ Monday.", options: ["since", "for"], a: "since"},
        {type: "mc", q: "She's been cooking ___ 2 hours.", options: ["for", "since"], a: "for"},
        {type: "input", q: "We've been here ___ noon.", a: "since"},
        {type: "match", q: "Duration examples:", pairs: [{left: "for a week", right: "7 days"}, {left: "since last week", right: "from past Monday"}, {left: "for ages", right: "long time"}, {left: "since April", right: "from month start"}], a: "completed"},
        {type: "order", q: "Traduce: 'Desde las 8am'", words: ["since", "8am"], a: "since 8am", extra: ["for morning"], tip: "Since + punto de inicio."},
        {type: "mc", q: "He's been working ___ this morning.", options: ["since", "for"], a: "since"},
        {type: "input", q: "I've been waiting ___ hours!", a: "for"},
        {type: "order", q: "Explain tired: I've been tiling ___ 6 hours ___ 7am!", words: ["for", "since"], a: "I've been tiling for 6 hours since 7am!", successTip: "DOUBLE TIME! Can use both: 'for 3 hours since noon'. Explains exhaustion perfectly! ⏳"}
      ]
    },

    // SET 6: PPC Scenarios
    {
      id: "m8-u2-s6",
      title: "Set 6: Ongoing Projects",
      context: `*DIY progress updates*\n\n"I've been renovating since January. Been painting for weeks!" Showing progress.\n\n*Home improvement group*\n\nBefore/after photos. Still messy.`,
      questions: [
        {type: "match", q: "PPC scenarios:", pairs: [{left: "I've been painting", right: "evidence: paint on clothes"}, {left: "She's been cooking", right: "evidence: kitchen mess"}, {left: "They've been gardening", right: "evidence: dirty hands"}, {left: "He's been studying", right: "evidence: tired eyes"}], a: "completed"},
        {type: "order", q: "Traduce: 'He estado renovando por meses'", words: ["I've", "been", "renovating", "for", "months."], a: "I've been renovating for months.", extra: ["I renovate", "since"], tip: "For + duration (months/weeks)."},
        {type: "mc", q: "She's been putting up shelves ___ yesterday.", options: ["since", "for"], a: "since"},
        {type: "mc", q: "We've been working ___ all day.", options: ["for", "since"], a: "for"},
        {type: "input", q: "I've been learning guitar ___ January.", a: "since"},
        {type: "match", q: "Home vocab:", pairs: [{left: "fitting carpet", right: "floor covering"}, {left: "making curtains", right: "window fabric"}, {left: "fixing bathtub", right: "repair plumbing"}, {left: "putting up shelves", right: "wall storage"}], a: "completed"},
        {type: "order", q: "Traduce: 'Desde la semana pasada'", words: ["since", "last", "week"], a: "since last week", extra: ["for week"], tip: "Since + past time reference."},
        {type: "mc", q: "They've been baking ___ this morning.", options: ["since", "for"], a: "since"},
        {type: "input", q: "He's been tiling ___ hours.", a: "for"},
        {type: "order", q: "Progress report: I've been renovating ___ March, painting ___ 3 weeks!", words: ["since", "for"], a: "I've been renovating since March, painting for 3 weeks!", successTip: "PROJECT UPDATE! PPC shows why tired + dirty. 'Been working all day!' Native explanation. 🛠️"}
      ]
    },

    // SET 7: PPC Questions (How long?)
    {
      id: "m8-u2-s7",
      title: "Set 7: Duration Questions",
      context: `*Meeting old friend*\n\n"How long have you been learning guitar? You sound amazing!" Asking about progress.\n\n*Music café*\n\nGuitar in corner. Skillful playing.`,
      questions: [
        {type: "match", q: "PPC question structure:", pairs: [{left: "How long", right: "have/has + S + been + ing"}, {left: "Have you been", right: "cooking?"}, {left: "How long has she", right: "been studying?"}, {left: "Since when", right: "have you been here?"}], a: "completed", successTip: "QUESTION FORMED! How long + have/has + subject + been + ing. Natural curiosity!"},
        {type: "order", q: "Traduce: '¿Cuánto tiempo has estado practicando?'", words: ["How", "long", "have", "you", "been", "practicing?"], a: "How long have you been practicing?", extra: ["How much time", "do you practice"], tip: "How long + PPC for duration."},
        {type: "mc", q: "___ she been learning piano?", options: ["How long has", "How long have"], a: "How long has"},
        {type: "mc", q: "Response: ___ 6 months.", options: ["For", "Since"], a: "For"},
        {type: "input", q: "How long have you ___ playing drums?", a: "been"},
        {type: "match", q: "Hobbies vocab:", pairs: [{left: "playing guitar", right: "strings"}, {left: "playing drums", right: "percussion"}, {left: "singing", right: "voice"}, {left: "playing piano", right: "keys"}], a: "completed"},
        {type: "order", q: "Traduce: '¿Desde cuándo tocas?'", words: ["How", "long", "have", "you", "been", "playing?"], a: "How long have you been playing?", extra: ["Since when play"], tip: "PPC para ongoing skill."},
        {type: "mc", q: "___ he been taking lessons?", options: ["Has", "Have"], a: "Has"},
        {type: "input", q: "How ___ has she been singing?", a: "long"},
        {type: "order", q: "Café chat: How long ___ you been learning guitar? -For 2 years!", words: ["have"], a: "How long have you been learning guitar? -For 2 years!", successTip: "DURATION MASTER! 'How long?' + for/since answer. Perfect conversation flow! 🎸"}
      ]
    },

    // SET 8: Evidence Questions
    {
      id: "m8-u2-s8",
      title: "Set 8: Evidence Questions",
      context: `*Noticing changes*\n\n"You're covered in flour! Have you been baking?" Evidence-based questions.\n\n*Kitchen scene*\n\nMess everywhere. Delicious smell.`,
      questions: [
        {type: "match", q: "Evidence → Question:", pairs: [{left: "Covered in paint", right: "Been painting?"}, {left: "Out of breath", right: "Been running?"}, {left: "Kitchen mess", right: "Been cooking?"}, {left: "Dirty hands", right: "Been gardening?"}], a: "completed"},
        {type: "order", q: "Traduce: '¿Has estado horneando?'", words: ["Have", "you", "been", "baking?"], a: "Have you been baking?", extra: ["Do you bake", "Are you baking"], tip: "Evidence visible → PPC question."},
        {type: "mc", q: "Wet hair! ___ swimming?", options: ["Have you been", "Did you go"], a: "Have you been"},
        {type: "mc", q: "You look tired. ___ working late?", options: ["Have you been", "Are you"], a: "Have you been"},
        {type: "input", q: "___ she been practicing? She sounds great!", a: "Has"},
        {type: "match", q: "Evidence clues:", pairs: [{left: "Sweating", right: "exercising"}, {left: "Paint stains", right: "decorating"}, {left: "Flour", right: "baking"}, {left: "Books open", right: "studying"}], a: "completed", successTip: "DETECTIVE TALK! See evidence → ask PPC. B2 natural observation skill!"},
        {type: "order", q: "Traduce: '¿Ha estado lloviendo?'", words: ["Has", "it", "been", "raining?"], a: "Has it been raining?", extra: ["Is it rain"], tip: "Weather evidence too!"},
        {type: "mc", q: "Dirty car! ___ off-roading?", options: ["Have you been", "Did you go"], a: "Have you been"},
        {type: "input", q: "Your voice! ___ you been singing?", a: "Have"},
        {type: "order", q: "Kitchen mess: Have you been ___ a cake? It smells amazing!", words: ["baking"], a: "Have you been baking a cake? It smells amazing!", successTip: "EVIDENCE PRO! 'Tired? Been working?' Perfect B2 question skill. Natural curiosity! 👀"}
      ]
    },

    // SET 9: Talents & Progress
    {
      id: "m8-u2-s9",
      title: "Set 9: Skills Development",
      context: `*Talent show prep*\n\n"How long have you been learning this? You're amazing!" Discussing practice time.\n\n*Rehearsal space*\n\nInstruments everywhere. Proud smiles.`,
      questions: [
        {type: "match", q: "Mix PP + PPC:", pairs: [{left: "I've already learned", right: "completed skill"}, {left: "I've been learning", right: "ongoing practice"}, {left: "Just finished", right: "recent completion"}, {left: "Still practicing", right: "continues now"}], a: "completed"},
        {type: "order", q: "Traduce: '¿Cuánto tiempo llevas tocando piano?'", words: ["How", "long", "have", "you", "been", "playing", "piano?"], a: "How long have you been playing piano?", extra: ["do you play"], tip: "Been + ing para ongoing."},
        {type: "mc", q: "I've ___ 3 languages already!", options: ["learned", "been learning"], a: "learned", tip: "PP simple = completion."},
        {type: "mc", q: "She's ___ for the concert.", options: ["been practicing", "practiced"], a: "been practicing", tip: "PPC = ongoing preparation."},
        {type: "input", q: "How long ___ you been taking lessons?", a: "have"},
        {type: "match", q: "Music vocab:", pairs: [{left: "saxophone", right: "woodwind"}, {left: "drums", right: "percussion"}, {left: "violin", right: "strings"}, {left: "trumpet", right: "brass"}], a: "completed"},
        {type: "order", q: "Traduce: 'He estado practicando por años'", words: ["I've", "been", "practicing", "for", "years."], a: "I've been practicing for years.", extra: ["I practice"], tip: "For + years = duration."},
        {type: "mc", q: "They've ___ a band!", options: ["just formed", "been forming"], a: "just formed"},
        {type: "input", q: "She's ___ taking singing lessons.", a: "been"},
        {type: "order", q: "Progress: I've been learning guitar ___ 5 years and just ___ my first song!", words: ["for", "finished"], a: "I've been learning guitar for 5 years and just finished my first song!", successTip: "TALENT TALK! Mix PPC duration + PP achievements. 'Been practicing ages!' 🎵"}
      ]
    },

    // SET 10: B2 Integration Mastery
    {
      id: "m8-u2-s10",
      title: "Set 10: Achievement Mastery",
      context: `*EducaT Adventure & Skills Expo*\n\nFull stories: "I've just returned from safari, been learning drums for years, still haven't tried skydiving yet."\n\n*Final presentations*\n\nEveryone sharing complete journeys.`,
      questions: [
        {type: "match", q: "Full mastery mix:", pairs: [{left: "just/already", right: "PP recent"}, {left: "yet/still", right: "PP pending"}, {left: "been + ing + for", right: "PPC duration"}, {left: "How long been", right: "PPC question"}], a: "completed"},
        {type: "order", q: "Traduce: 'Acabo de terminar mi proyecto que he estado haciendo por meses'", words: ["I've", "just", "finished", "the", "project", "I've", "been", "doing", "for", "months."], a: "I've just finished the project I've been doing for months.", extra: ["I finish"], tip: "Mix PP + PPC naturally."},
        {type: "mc", q: "Evidence + duration: You're exhausted! ___ all day?", options: ["Have you been working", "Did you work"], a: "Have you been working"},
        {type: "mc", q: "I've already ___ 5 countries but ___ tried skydiving.", options: ["visited/haven't yet", "been visiting/still"], a: "visited/haven't yet"},
        {type: "input", q: "How long have you ___ renovating?", a: "been"},
        {type: "match", q: "Complete scenarios:", pairs: [{left: "Covered in paint", right: "Been decorating for hours"}, {left: "Out of breath", right: "Just finished running"}, {left: "Kitchen mess", right: "Been baking since morning"}, {left: "Proud smile", right: "Already completed project"}], a: "completed"},
        {type: "order", q: "Traduce: '¿Cuánto tiempo has estado aprendiendo inglés?'", words: ["How", "long", "have", "you", "been", "learning", "English?"], a: "How long have you been learning English?", extra: ["do you learn"], tip: "PPC for ongoing education."},
        {type: "mc", q: "Timeline: I've ___ 3 adventures already this year!", options: ["completed", "been completing"], a: "completed"},
        {type: "input", q: "Still haven't ___ the Great Barrier Reef yet.", a: "seen"},
        {type: "order", q: "Ultimate story: I've been learning guitar ___ 2 years, just ___ my first concert, but still ___ performed solo yet!", words: ["for", "finished", "haven't"], a: "I've been learning guitar for 2 years, just finished my first concert, but still haven't performed solo yet!", successTip: "M8-U2 CONQUERED! PP adverbs + PPC duration + questions = B2 fluent storytelling. 'Been training ages, just achieved goal!' Native mastery! 🏆🎸🌍"}
      ]
    }
  ],

  readingSet: {
    id: "m8-u2-reading",
    title: "Reading: Adventures & Skills Journey",
    category: "reading",
    questions: [
      {
        type: "passage",
        passage: `**My Australian Adventure**\n\nI've just returned from Australia! I've already climbed Harbour Bridge and learned to surf at Bondi Beach. I still haven't seen any dolphins yet, but I've been trying every day. The trip has been incredible so far!`
      },
      {
        type: "mc",
        q: "What has the speaker already done?",
        options: ["Climbed Harbour Bridge", "Seen dolphins", "Tried skydiving"],
        a: "Climbed Harbour Bridge"
      },
      {
        type: "order",
        q: "Traduce: 'Acabo de regresar de Australia'",
        words: ["I've", "just", "returned", "from", "Australia."],
        a: "I've just returned from Australia.",
        tip: "Just = recent completion."
      },
      {
        type: "passage",
        passage: `**Home Renovation Update**\n\nExhausted! I've been renovating my house for three months. Since January I've been painting, tiling, and fixing everything. I've already finished the kitchen but still haven't started the bedroom yet. Worth it!`
      },
      {
        type: "mc",
        q: "How long renovating?",
        options: ["3 months", "1 month", "3 weeks"],
        a: "3 months"
      },
      {
        type: "input",
        q: "Since ___?",
        a: "January"
      },
      {
        type: "mc",
        q: "What's finished?",
        options: ["Kitchen", "Bedroom", "Bathroom"],
        a: "Kitchen"
      },
      {
        type: "passage",
        passage: `**Music Journey**\n\n"How long have you been playing guitar?" my friend asked. "For five years, since high school!" I replied. I've just finished my first concert and I've already been invited to play at a festival. Still haven't recorded an album yet, but I've been writing songs for months!`
      },
      {
        type: "order",
        q: "Traduce: '¿Cuánto tiempo has estado tocando?'",
        words: ["How", "long", "have", "you", "been", "playing?"],
        a: "How long have you been playing?",
        successTip: "READING COMPLETE! PP adverbs + PPC durations mastered in context."
      },
      {
        type: "mc",
        q: "What just finished?",
        options: ["First concert", "Album", "School"],
        a: "First concert"
      }
    ]
  },

  reviewSet: {
    id: "m8-u2-review",
    title: "Unit 2 Mastery Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Adverb positions:",
        pairs: [
          {left: "just/already", right: "before main verb"},
          {left: "yet", right: "sentence end negative"},
          {left: "still", right: "after subject"},
          {left: "for/since", right: "with PPC"}
        ],
        a: "completed"
      },
      {
        type: "order",
        q: "Traduce mastery: 'He estado trabajando por horas desde las 8am'",
        words: ["I've", "been", "working", "for", "hours", "since", "8am."],
        a: "I've been working for hours since 8am.",
        tip: "Double time marker allowed."
      },
      {
        type: "mc",
        q: "Evidence question: Covered in flour! ___?",
        options: ["Have you been baking", "Did you bake"],
        a: "Have you been baking"
      },
      {
        type: "input",
        q: "I've ___ finished my project!",
        a: "just"
      },
      {
        type: "match",
        q: "Vocabulary mix:",
        pairs: [
          {left: "scuba diving/snorkeling", right: "underwater sports"},
          {left: "tiling/painting", right: "home improvements"},
          {left: "guitar/drums", right: "musical instruments"},
          {left: "for/since", right: "duration markers"}
        ],
        a: "completed"
      },
      {
        type: "order",
        q: "Complex: I've already tried safari but still ___ skydiving yet.",
        words: ["haven't", "tried"],
        a: "I've already tried safari but still haven't tried skydiving yet."
      },
      {
        type: "mc",
        q: "How long ___ learning English?",
        options: ["have you been", "are you"],
        a: "have you been"
      },
      {
        type: "input",
        q: "She's been cooking ___ 2 hours.",
        a: "for"
      },
      {
        type: "match",
        q: "PP vs PPC:",
        pairs: [
          {left: "I've finished", right: "completed"},
          {left: "I've been working", right: "ongoing"},
          {left: "Just done", right: "recent PP"},
          {left: "Been doing for hours", right: "duration PPC"}
        ],
        a: "completed"
      },
      {
        type: "order",
        q: "Ultimate mastery: How long have you been learning? -___ 5 years, just ___ my exam!",
        words: ["For", "passed"],
        a: "How long have you been learning? -For 5 years, just passed my exam!",
        successTip: "M8-U2 MASTERED! PP adverbs (just/already/yet/still) + PPC (been + ing + for/since) + Questions (How long?) = B2 achievement storytelling complete! 🌟🎯"
      }
    ]
  }
};
